import React, { forwardRef } from 'react';
import { Button, Icon, StackItem } from '@leagueplatform/genesis-core';
import { dropdownMenuTriggerStyles } from './select-action-menu.styles';

/**
 * Props for the SelectActionMenuButton component.
 *
 * @property {boolean} [disabled] - Optional. Whether the button is disabled.
 * @property {string} menuLabel - The accessible label for the menu button. Used for aria-label.
 * @property {React.ReactNode} triggerLabel - The visible label or content displayed on the button.
 * @property {any} [key: string] - Additional Radix UI props can be passed and will be forwarded to the underlying button.
 */
interface SelectActionMenuButtonProps {
  disabled?: boolean;
  menuLabel: string;
  triggerLabel: React.ReactNode;
  // Allow for additional Radix UI props
  [key: string]: any;
}

/**
 * A button component used to trigger the SelectActionMenu dropdown.
 *
 * This component is designed to be used as a child to `SelectActionMenu` (in Radix UI's DropdownMenu.Trigger)
 * and displays a button with the provided label and a dropdown icon.
 * It follows the tertiary button style and includes appropriate accessibility attributes.
 *
 * @param {SelectActionMenuButtonProps} props - The component props
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref for the button element
 * @returns {JSX.Element} The rendered button component
 */
export const SelectActionMenuButton = forwardRef<
  HTMLButtonElement,
  SelectActionMenuButtonProps
>((props, ref) => {
  const { disabled, menuLabel, triggerLabel, ...radixProps } = props;

  return (
    <Button
      ref={ref}
      priority="tertiary"
      size="medium"
      aria-label={menuLabel}
      disabled={disabled}
      data-testid="select-action-menu-button"
      css={dropdownMenuTriggerStyles}
      {...radixProps}
    >
      <StackItem
        css={{
          flexDirection: 'row',
          typography: '$bodyTwo',
          color: '$onSurfaceTextPrimary',
        }}
      >
        {triggerLabel}
        <Icon
          icon="interfaceChevronDown"
          css={{ marginInlineStart: '$half' }}
          size="1.25rem"
          tint="$interactiveActionSubdued"
        />
      </StackItem>
    </Button>
  );
});
